import { forwardRef, useCallback, useContext } from 'react';
import { AText, Box } from '@alf/uikit';
import { LOG_ACTIONS, logAction, useLogAmplitude } from '@namespace/analytics';
import { getCurrencySymbol } from '@namespace/helpers';
import { useHistory } from '@namespace/router';
import { USER_ROLES, UserContext } from '@namespace/user';
import { CASINO_TYPE_MAP, REAL_MODE } from '../../../../constants';
import {
  useCasinoSettings,
  useGetFeedName,
  useGetLobbyPageUrl
} from '../../../../hooks';
import { useGetRecentlyPlayedGamesIds } from '../../../../store/selectors';
import GameImage from '../../../Game/GameImage';

import styles from './index.module.css';

export const RealTimeWinsItem = forwardRef(
  (
    {
      userId,
      amount,
      currency,
      game,
      casinoType,
      getGameURL,
      rotationInterval,
      style
    },
    ref
  ) => {
    const { push } = useHistory();
    const lobbyPageUrl = useGetLobbyPageUrl(casinoType);
    const logAmplitude = useLogAmplitude();
    const [user] = useContext(UserContext);
    const { role } = user;

    const feedName = useGetFeedName(casinoType);
    const { suggesterServiceEntityName } = useCasinoSettings(casinoType);
    const recentlyPlayedGamesIds = useGetRecentlyPlayedGamesIds(
      suggesterServiceEntityName,
      feedName
    );

    const {
      gameName = '',
      providerId = '',
      gameId = '',
      gameCode = '',
      launchGameId = '',
      providerName = ''
    } = game || {};

    const attributes = {
      game_name: gameName,
      provider_name: providerName,
      section_name: CASINO_TYPE_MAP[casinoType],
      category_name: 'Current winners',
      game_mode: 'real_money'
    };

    let isRecentlyPlayedGame;

    if (USER_ROLES.USER === role) {
      isRecentlyPlayedGame = recentlyPlayedGamesIds?.includes(gameId);
      attributes.recently_played = isRecentlyPlayedGame ? 'yes' : 'no';
    }

    const onGameClick = useCallback(() => {
      logAmplitude({
        actionName: LOG_ACTIONS.CASINO_GAME_OPEN,
        attributes
      });
      logAction(LOG_ACTIONS.REAL_TIME_WINS_OPEN_GAME, attributes);

      const gameUrl = getGameURL({
        gameCode: gameId,
        gameProvider: providerId,
        playMode: REAL_MODE,
        serviceURL: lobbyPageUrl,
        type: 'launch'
      });

      push(gameUrl);
    }, [
      casinoType,
      gameCode,
      gameName,
      launchGameId,
      gameId,
      getGameURL,
      lobbyPageUrl,
      providerId,
      push
    ]);

    if (!game) {
      return null;
    }

    return (
      <Box
        ref={ref}
        className={styles.wrapper}
        onClick={onGameClick}
        style={{
          ...style,
          transition: `transform ${rotationInterval}ms linear`
        }}
      >
        <Box className={styles.absolute}>
          <Box className={styles.container}>
            <Box className={styles.gameImage}>
              <GameImage
                onImageLoad={() => {}}
                game={game}
                isWideGame={false}
                turnOffJackpotTickers={true}
              />
            </Box>
            <Box className={styles.content}>
              <AText breed="B" size="s" color="txt_color_3">
                {userId}
              </AText>
              <AText
                color="txt_color_7"
                className={styles.gameName}
                breed="R"
                size="s"
              >
                {gameName}
              </AText>
              <Box className={styles.contentPrice} gap="xxs">
                <AText breed="B" color="txt_color_3" size="xl">
                  {amount}
                </AText>
                <AText breed="B" color="txt_color_3" size="xl">
                  {getCurrencySymbol(currency)}
                </AText>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);
